'use client';

import { translateInline } from '@/globals/translate/translateInline';
import { classes } from '@/uiPrimitives/base/classes.helpers';
import { box } from '@/uiPrimitives/layout/box';
import { shelf } from '@/uiPrimitives/layout/shelf';
import { textBlock, textStyle } from '@/uiPrimitives/typography/text';
import { ReactNode, createContext, use, useState } from 'react';

const CurrentViewButton = ({ children }: { children: ReactNode }) => (
	<span
		className={classes(
			box({
				paddingBlock: '-1 | small',
				paddingInline: '1 | h3',
				borderWidth: '3px',
				backgroundColor: 'negative',
			}),
			textBlock({ fontWeight: 'bold' }),
		)}
	>
		{children}
	</span>
);

const SwitchActiveViewButton = ({
	children,
	onClick,
}: {
	children: ReactNode;
	onClick: () => void;
}) => (
	<button
		onClick={onClick}
		className={classes(
			box({ paddingBlock: '-1 | small', paddingInline: '1 | h3' }),
			textBlock({ color: 'positiveBlue', textDecorationLine: 'underline' }),
		)}
	>
		{children}
	</button>
);

type View = 'graph' | 'table';

const CbsViewContext = createContext<View | undefined>(undefined);

export const CbbsGraphViewSwitcher = ({
	children,
}: {
	children: ReactNode;
}) => {
	const [activeView, setActiveView] = useState<View>('graph');
	const graphLabel = translateInline({ en: 'Graph', nl: 'Grafiek' });
	const tableLabel = translateInline({ en: 'Table', nl: 'Tabel' });

	return (
		<>
			<div
				className={classes(
					shelf({}),
					textStyle({ capSize: '-1 | small' }),
					box({
						backgroundColor: 'negativeBlue',
						borderWidth: '1px',
						paddingInline: 'negative 1 | h3',
					}),
				)}
			>
				{activeView === 'graph' ? (
					<CurrentViewButton>{graphLabel}</CurrentViewButton>
				) : (
					<SwitchActiveViewButton onClick={() => setActiveView('graph')}>
						{graphLabel}
					</SwitchActiveViewButton>
				)}
				{activeView === 'table' ? (
					<CurrentViewButton>{tableLabel}</CurrentViewButton>
				) : (
					<SwitchActiveViewButton onClick={() => setActiveView('table')}>
						{tableLabel}
					</SwitchActiveViewButton>
				)}
			</div>
			<CbsViewContext value={activeView}>{children}</CbsViewContext>
		</>
	);
};

export const useCbsActiveView = () => {
	const context = use(CbsViewContext);
	if (context === undefined) {
		throw new Error('useCbsView must be used within a CbsGraphProvider');
	}
	return context;
};
